/**
 * Debug logging utility with privacy protection
 *
 * SECURITY: This module ensures sensitive data is never logged in production.
 * All debug logs are automatically disabled in production builds.
 *
 * Usage:
 *   import { debug } from './debug';
 *   debug.log('API', 'Request sent', { url, method }); // Safe - no PII
 *   debug.log('API', 'User loaded', data, { redact: ['email', 'phone_number'] }); // Redacts sensitive fields
 */

import { config } from '@/src/config';

// Fields that should always be redacted when logging user data
const SENSITIVE_FIELDS = [
  'password',
  'phone_number',
  'phone',
  'email',
  'ssn',
  'social_security',
  'credit_card',
  'card_number',
  'cvv',
  'date_of_birth',
  'dob',
  'medicaid_id',
  'access_token',
  'refresh_token',
  'api_key',
  'secret',
  'street_address',
  'address',
] as const;

type SensitiveField = typeof SENSITIVE_FIELDS[number];

interface LogOptions {
  /** Additional fields to redact beyond the default sensitive fields */
  redact?: string[];
  /** If true, skip all redaction (use with caution, only for non-sensitive data) */
  skipRedaction?: boolean;
}

/**
 * Check if we're in a development environment
 * Logs are only output in development/local modes
 */
const isDev = (): boolean => {
  return config.isDevelopment || config.isLocal || import.meta.env.DEV;
};

/**
 * Redact sensitive fields from an object for safe logging
 */
const redactSensitiveData = <T>(data: T, additionalFields: string[] = []): T => {
  if (data === null || data === undefined) {
    return data;
  }

  if (typeof data !== 'object') {
    return data;
  }

  if (Array.isArray(data)) {
    return data.map(item => redactSensitiveData(item, additionalFields)) as T;
  }

  const fieldsToRedact = new Set([...SENSITIVE_FIELDS, ...additionalFields]);
  const redacted = { ...data } as Record<string, unknown>;

  for (const key of Object.keys(redacted)) {
    const lowerKey = key.toLowerCase();

    // Check if this field should be redacted
    if (fieldsToRedact.has(lowerKey) || fieldsToRedact.has(key)) {
      redacted[key] = '[REDACTED]';
    } else if (typeof redacted[key] === 'object' && redacted[key] !== null) {
      // Recursively redact nested objects
      redacted[key] = redactSensitiveData(redacted[key], additionalFields);
    }
  }

  return redacted as T;
};

/**
 * Format log arguments, applying redaction as needed
 */
const formatArgs = (args: unknown[], options: LogOptions = {}): unknown[] => {
  if (options.skipRedaction) {
    return args;
  }

  return args.map(arg => {
    if (typeof arg === 'object' && arg !== null) {
      return redactSensitiveData(arg, options.redact);
    }
    return arg;
  });
};

/**
 * Debug logger that only outputs in development mode
 * Automatically redacts sensitive fields from logged objects
 */
export const debug = {
  /**
   * Log a debug message (only in development)
   * @param category - Log category (e.g., 'API', 'Auth', 'UI')
   * @param message - Log message
   * @param args - Additional data to log (sensitive fields auto-redacted)
   */
  log: (category: string, message: string, ...args: unknown[]): void => {
    if (!isDev()) return;

    const options = args.length > 0 && typeof args[args.length - 1] === 'object' &&
      args[args.length - 1] !== null && 'redact' in (args[args.length - 1] as object)
      ? args.pop() as LogOptions
      : {};

    const safeArgs = formatArgs(args, options);
    console.log(`[${category}]`, message, ...safeArgs);
  },

  /**
   * Log a warning (only in development)
   */
  warn: (category: string, message: string, ...args: unknown[]): void => {
    if (!isDev()) return;

    const options = args.length > 0 && typeof args[args.length - 1] === 'object' &&
      args[args.length - 1] !== null && 'redact' in (args[args.length - 1] as object)
      ? args.pop() as LogOptions
      : {};

    const safeArgs = formatArgs(args, options);
    console.warn(`[${category}]`, message, ...safeArgs);
  },

  /**
   * Log an error (always logged, but with redaction in production)
   * Errors are important for debugging even in production
   */
  error: (category: string, message: string, ...args: unknown[]): void => {
    const options = args.length > 0 && typeof args[args.length - 1] === 'object' &&
      args[args.length - 1] !== null && 'redact' in (args[args.length - 1] as object)
      ? args.pop() as LogOptions
      : {};

    // Always redact in production, optionally in development
    const safeArgs = isDev() && options.skipRedaction
      ? args
      : formatArgs(args, options);

    console.error(`[${category}]`, message, ...safeArgs);
  },

  /**
   * Log API request details (development only)
   * Automatically handles common API logging patterns
   */
  apiRequest: (method: string, url: string, options?: { body?: unknown }): void => {
    if (!isDev()) return;

    const logData: Record<string, unknown> = { method, url };
    if (options?.body) {
      logData.body = redactSensitiveData(options.body);
    }
    console.log('🌐 [API Request]', logData);
  },

  /**
   * Log API response details (development only)
   * Automatically redacts sensitive response data
   */
  apiResponse: (status: number, ok: boolean, data?: unknown): void => {
    if (!isDev()) return;

    const logData: Record<string, unknown> = { status, ok };
    if (data !== undefined) {
      logData.data = redactSensitiveData(data);
    }
    console.log('🌐 [API Response]', logData);
  },

  /**
   * Log API error details (development only for full details, redacted in production)
   */
  apiError: (status: number, error: unknown): void => {
    const redactedError = redactSensitiveData(error);
    if (isDev()) {
      console.error('🌐 [API Error]', { status, error: redactedError });
    } else {
      // In production, log minimal info
      console.error('🌐 [API Error]', { status, message: 'Request failed' });
    }
  },
};

/**
 * Type guard to check if an object has sensitive fields
 * Useful for conditional logging decisions
 */
export const hasSensitiveFields = (obj: unknown): boolean => {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  const keys = Object.keys(obj).map(k => k.toLowerCase());
  return SENSITIVE_FIELDS.some(field => keys.includes(field));
};

export default debug;
